/**
 * Ops Slice 1 — compact attachment strip for a paperwork item (or for the
 * deal-level vault when `paperworkItemId` is `null`). Editor roles see an
 * "Attach file" input chip and a × remove button per tile. Image MIME types
 * render as 56x56 thumbnails using the signed read URL; PDFs render as a
 * file icon tile with a truncated display name.
 *
 * Mirrors the Slice 17 ads media chip pattern, simplified for paperwork.
 */
import { useRef, useState } from "react";
import { FileText, X } from "lucide-react";
import { cardMutedTextClass, cardSurfaceClass } from "@/components/common/surfaceClasses";
import {
  deleteOpsPaperworkAttachment,
  uploadOpsPaperworkAttachment,
  type OpsPaperworkAttachment,
} from "@/lib/opsPaperworkAttachmentsClient";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);

type Props = {
  dealId: string;
  paperworkItemId: string | null;
  attachments: OpsPaperworkAttachment[];
  canEdit: boolean;
  onChanged: () => Promise<void> | void;
};

function truncate(s: string | null, n: number): string {
  if (!s) return "file";
  return s.length > n ? `${s.slice(0, n - 1)}\u2026` : s;
}

export function OpsPaperworkAttachmentStrip(props: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPickFile(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const r = await uploadOpsPaperworkAttachment(props.dealId, props.paperworkItemId, file);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      await props.onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(id: string) {
    setError(null);
    setBusy(true);
    try {
      const r = await deleteOpsPaperworkAttachment(id);
      if (!r.ok) {
        setError(r.error);
        return;
      }
      await props.onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-start gap-2">
        {props.attachments.map((a) => (
          <div
            key={a.id}
            className={`relative group rounded border ${cardSurfaceClass}`}
            title={a.display_name ?? a.object_path}
          >
            {IMAGE_MIMES.has(a.content_type) && a.signed_url ? (
              <a href={a.signed_url} target="_blank" rel="noreferrer noopener" className="block">
                <img
                  src={a.signed_url}
                  alt={a.display_name ?? "attachment"}
                  className="h-14 w-14 rounded object-cover"
                  loading="lazy"
                />
              </a>
            ) : (
              <a
                href={a.signed_url ?? "#"}
                target={a.signed_url ? "_blank" : undefined}
                rel="noreferrer noopener"
                className={`flex h-14 w-14 flex-col items-center justify-center gap-0.5 rounded p-1 text-[10px] ${cardMutedTextClass}`}
              >
                <FileText className="h-5 w-5 text-gray-500" />
                <span className="block max-w-[52px] truncate">{truncate(a.display_name, 9)}</span>
              </a>
            )}
            {props.canEdit ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void onRemove(a.id)}
                className={`absolute -top-1.5 -right-1.5 rounded-full border p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40 ${cardSurfaceClass} ${cardMutedTextClass}`}
                aria-label={`Remove ${a.display_name ?? "attachment"}`}
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        ))}
        {props.canEdit ? (
          <label className={`inline-flex h-14 w-14 cursor-pointer items-center justify-center rounded border border-dashed text-[10px] hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-400 dark:hover:bg-zinc-800/60 ${cardMutedTextClass}`}>
            {busy ? "…" : "Attach file"}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              disabled={busy}
              onChange={(e) => void onPickFile(e)}
            />
          </label>
        ) : null}
      </div>
      {error ? <div className="text-xs text-red-600">{error}</div> : null}
    </div>
  );
}
